import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { PasswordInput } from "@/components/account/LoginForm";

export const RegisterForm = ({ onSuccess }) => {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const field = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Debes aceptar las Condiciones y la Política de privacidad");
      return;
    }
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`¡Bienvenido/a, ${u.name?.split(" ")[0] || ""}! Tu cuenta está lista.`);
      onSuccess?.(u);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="customer-register-form">
      <div className="space-y-2">
        <Label htmlFor="reg-name">Nombre completo</Label>
        <Input id="reg-name" data-testid="customer-register-name-input" required minLength={2} autoComplete="name"
          value={form.name} onChange={field("name")} placeholder="Nombre y apellidos" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reg-email">Correo electrónico</Label>
          <Input id="reg-email" data-testid="customer-register-email-input" type="email" required autoComplete="email"
            value={form.email} onChange={field("email")} placeholder="tu@correo.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reg-phone">Teléfono <span className="text-slate-400 font-normal">(opcional)</span></Label>
          <Input id="reg-phone" data-testid="customer-register-phone-input" autoComplete="tel"
            value={form.phone} onChange={field("phone")} placeholder="6XX XXX XXX" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="reg-password">Contraseña <span className="text-slate-400 font-normal">(mínimo 8 caracteres)</span></Label>
        <PasswordInput id="reg-password" testId="customer-register-password-input" value={form.password}
          onChange={field("password")} autoComplete="new-password" />
      </div>
      <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer" htmlFor="reg-terms">
        <Checkbox id="reg-terms" data-testid="customer-register-terms-checkbox" checked={accepted}
          onCheckedChange={(v) => setAccepted(v === true)} className="mt-0.5" />
        <span>
          Acepto las{" "}
          <Link to="/condiciones" target="_blank" className="text-cyan-700 underline underline-offset-2">Condiciones del servicio</Link>{" "}
          y la{" "}
          <Link to="/privacidad" target="_blank" className="text-cyan-700 underline underline-offset-2">Política de privacidad</Link>.
        </span>
      </label>
      <Button type="submit" disabled={loading} data-testid="customer-register-submit-btn"
        className="w-full h-11 rounded-full bg-cyan-500 hover:bg-cyan-600">
        <UserPlus className="h-4 w-4 mr-2" /> {loading ? "Creando cuenta..." : "Crear cuenta"}
      </Button>
    </form>
  );
};
